<template>
  <div class="lead-form">
    <!-- Abas: Coleta / Registradas -->
    <div class="lf-tabs">
      <button
        type="button"
        class="lf-tab"
        :class="{ active: tab === 'coleta' }"
        @click="tab = 'coleta'"
      >Coleta</button>
      <button
        type="button"
        class="lf-tab"
        :class="{ active: tab === 'historico' }"
        @click="tab = 'historico'"
      >
        Registradas
        <span v-if="submissions.length" class="lf-tab-count">{{ submissions.length }}</span>
      </button>
    </div>

    <!-- Corpo rolável -->
    <div class="lf-body">
      <!-- Aba Coleta: formulário dinâmico (vem da base) -->
      <div v-show="tab === 'coleta'">
        <div v-if="fields.length === 0" class="lead-muted">
          Nenhum campo configurado. Defina os campos em
          <strong>Controle de acesso → Configurar Formulário de Coleta de Lead</strong>.
        </div>

        <div
          v-for="field in visibleFields"
          :key="field.id || field.name"
          class="field"
        >
          <label>
            {{ field.label || field.name }}
            <span v-if="field.required">*</span>
          </label>

          <!-- Textarea -->
          <textarea
            v-if="getType(field) === 'textarea'"
            v-model="formData[field.name]"
            rows="3"
            :placeholder="field.placeholder || ''"
          ></textarea>

          <!-- Select (seleção única) -->
          <select
            v-else-if="getType(field) === 'select'"
            v-model="formData[field.name]"
            class="lead-select"
          >
            <option value="">Selecione...</option>
            <option v-for="opt in optionsOf(field)" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>

          <!-- Multiselect (dropdown de tags) -->
          <i-multiselect
            v-else-if="getType(field) === 'multiselect'"
            v-model="formData[field.name]"
            :options="optionsOf(field)"
            placeholder="Selecione..."
          />

          <!-- Radio (seleção única) -->
          <div v-else-if="getType(field) === 'radio'" class="lead-choices">
            <label v-for="opt in optionsOf(field)" :key="opt.value" class="lead-check">
              <input type="radio" :value="opt.value" v-model="formData[field.name]" />
              <span>{{ opt.label }}</span>
            </label>
          </div>

          <!-- Checkbox único -->
          <label v-else-if="getType(field) === 'checkbox'" class="lead-check lead-check--single">
            <input type="checkbox" v-model="formData[field.name]" />
            <span>{{ field.placeholder || 'Sim' }}</span>
          </label>

          <!-- Data / Data e hora — texto com máscara (mesmo padrão do formulário público) -->
          <input
            v-else-if="getType(field) === 'date'"
            type="text"
            inputmode="numeric"
            v-mask="'##/##/####'"
            placeholder="DD/MM/AAAA"
            v-model="formData[field.name]"
          />
          <input
            v-else-if="getType(field) === 'datetime'"
            type="text"
            inputmode="numeric"
            v-mask="'##/##/#### ##:##'"
            placeholder="DD/MM/AAAA HH:MM"
            v-model="formData[field.name]"
          />

          <!-- Texto / e-mail / número / etc -->
          <input
            v-else
            :type="inputTypeOf(field)"
            v-model="formData[field.name]"
            :placeholder="field.placeholder || ''"
          />

          <div v-if="field.help_text" class="field-hint">{{ field.help_text }}</div>
        </div>
      </div>

      <!-- Aba Registradas: histórico de coletas -->
      <div v-show="tab === 'historico'">
        <div v-if="submissions.length === 0" class="history-empty">
          <div class="history-empty-icon">
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
              <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="history-empty-text">Nenhuma coleta registrada</div>
          <div class="history-empty-sub">As coletas deste participante aparecerão aqui.</div>
        </div>

        <div v-else>
          <div v-for="sub in submissions" :key="sub.id" class="history-item">
            <div class="h-icon">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="h-info">
              <div class="h-top">
                <div class="h-name">{{ sub.created_by_name || 'Operador' }}</div>
                <div class="h-date">{{ $moment(sub.created_at).format('DD/MM HH:mm') }}</div>
              </div>
              <div class="h-fields">
                <div v-for="(ans, i) in answersOf(sub)" :key="i" class="h-field-row">
                  <strong>{{ ans.label }}:</strong> {{ ans.value }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rodapé fixo: submit (só na aba Coleta e quando há campos) -->
    <div v-if="tab === 'coleta' && fields.length > 0" class="lf-footer">
      <button
        type="button"
        class="lf-submit"
        :disabled="saving"
        @click="submit"
      >
        <svg v-if="!saving" width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 8l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ saving ? 'Salvando...' : 'Registrar coleta' }}
      </button>
    </div>
  </div>
</template>

<script>
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { CHECK_IN_MODULE } from '@/modules/check-in'
import { fetchLeadSubmissions, saveLeadSubmission } from '@/modules/check-in/service/leadForm'
import { shouldShowField } from '@/modules/check-in/utils/conditionEvaluator'
import IMultiselect from '@/components/inputs/IMultiselect.vue'

const DISPLAY_ONLY = ['disclaimer', 'instrucoes', 'fake_ticket', 'section']

export default {
  name: 'info-check-in-lead-form',
  components: { IMultiselect },
  props: {
    form: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    tab: 'coleta',
    saving: false,
    formData: {},
    submissions: [],
    historyOpen: false
  }),
  computed: {
    currentId () {
      return this.$store.state[CHECK_IN_MODULE].showDetailId
    },
    fields () {
      const raw = (this.form && this.form.fields) || []
      return raw.filter(f => f && !DISPLAY_ONLY.includes(this.getType(f)))
    },
    visibleFields () {
      return this.fields.filter(f => shouldShowField(f, this.formData, this.fields))
    }
  },
  watch: {
    currentId () {
      this.tab = 'coleta'
      this.resetFormData()
      this.loadSubmissions()
    },
    form () {
      this.resetFormData()
    }
  },
  mounted () {
    this.resetFormData()
    this.loadSubmissions()
  },
  methods: {
    getType (field) {
      return field.type ||
        (field.fieldType && field.fieldType.name) ||
        (field.field_type && field.field_type.name) ||
        (field.config && field.config.type) ||
        'text'
    },
    inputTypeOf (field) {
      const t = this.getType(field)
      if (t === 'email') return 'email'
      if (['number', 'decimal', 'percentage'].includes(t)) return 'number'
      return 'text'
    },
    optionsOf (field) {
      const raw = field.options || (field.config && field.config.options) || []
      return raw.map(o => {
        if (o && typeof o === 'object') return { label: o.label != null ? o.label : o.value, value: o.value }
        return { label: o, value: o }
      })
    },
    resetFormData () {
      const data = {}
      this.fields.forEach(f => {
        const t = this.getType(f)
        if (t === 'multiselect') data[f.name] = []
        else if (t === 'checkbox') data[f.name] = false
        else data[f.name] = ''
      })
      this.formData = data
    },
    async loadSubmissions () {
      if (!this.currentId) {
        this.submissions = []
        return
      }
      try {
        this.submissions = await fetchLeadSubmissions(this.currentId)
      } catch (e) {
        this.submissions = []
      }
    },
    answersOf (sub) {
      const data = sub && sub.data
      if (!Array.isArray(data)) return []
      return data.map(a => ({
        label: a.label || a.name,
        value: this.displayValue(a.value)
      }))
    },
    displayValue (v) {
      if (Array.isArray(v)) return v.join(', ') || '—'
      if (v === true) return 'Sim'
      if (v === false) return 'Não'
      if (v === null || v === undefined || v === '') return '—'
      return String(v)
    },
    async submit () {
      const missing = this.visibleFields.filter(f => {
        if (!f.required) return false
        const val = this.formData[f.name]
        if (Array.isArray(val)) return val.length === 0
        return val === '' || val === null || val === undefined || val === false
      })
      if (missing.length) {
        errorMessage('Preencha os campos obrigatórios: ' + missing.map(f => f.label || f.name).join(', '))
        return
      }

      const answers = this.visibleFields.map(f => ({
        name: f.name,
        label: f.label || f.name,
        value: this.formData[f.name]
      }))

      this.saving = true
      try {
        await saveLeadSubmission(this.currentId, answers)
        successMessage('Coleta de lead registrada.')
        // Sinaliza a lista pra marcar o item e atualizar o contador "Coletados".
        this.$store.commit(CHECK_IN_MODULE + '/markLeadCollected', this.currentId)
        this.resetFormData()
        await this.loadSubmissions()
      } catch (e) {
        errorMessage('Ocorreu um erro ao registrar a coleta de lead.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.lead-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* ── Abas ── */
.lf-tabs {
  display: flex;
  border-bottom: 1px solid #eef0f4;
  flex-shrink: 0;
}

.lf-tab {
  flex: 1;
  padding: 14px 16px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #b8bcc8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.lf-tab.active {
  color: rgb(37, 99, 235);
  border-bottom-color: rgb(37, 99, 235);
}

.lf-tab:hover:not(.active) { color: #7b8294; }

.lf-tab-count {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  background: #eef0f4;
  color: #9ca3b4;
}

.lf-tab.active .lf-tab-count {
  background: rgba(37, 99, 235, 0.1);
  color: rgb(37, 99, 235);
}

/* ── Corpo rolável ── */
.lf-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
}

.lf-body::-webkit-scrollbar { width: 4px; }
.lf-body::-webkit-scrollbar-track { background: transparent; }
.lf-body::-webkit-scrollbar-thumb { background: #a2adb7; border-radius: 7px; }

.lead-muted {
  font-size: 13px;
  color: #74788d;
}

/* ── Campos do formulário dinâmico ── */
.field {
  margin-bottom: 16px;
}

.field label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #4a5068;
  margin-bottom: 6px;
}

.field label span { color: #ef4444; }

.field input,
.field textarea,
.field select {
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid #e4e7ed;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: #1a1d26;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.field input:focus,
.field textarea:focus,
.field select:focus {
  border-color: rgb(37, 99, 235);
}

.field input::placeholder,
.field textarea::placeholder {
  color: #c0c4d0;
}

.field textarea {
  resize: vertical;
  min-height: 80px;
}

.field-hint {
  font-size: 0.6875rem;
  color: #b8bcc8;
  margin-top: 4px;
}

/* Select — seta própria (remove aparência nativa que quebra no mobile) */
.lead-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 38px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%236b7280' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M4 6l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 13px center;
  background-size: 13px;
}

/* Radio / Checkbox */
.lead-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 2px;
}

.lead-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #374151;
  margin: 0;
  cursor: pointer;
}

.lead-check input {
  width: 16px;
  height: 16px;
}

.lead-check--single { padding-top: 2px; }

/* Multiselect — acompanha os demais campos */
.lead-form >>> .ms-trigger {
  min-height: 44px;
  max-height: none;
  border-radius: 10px;
  border: 1.5px solid #e4e7ed;
}

/* ── Histórico (aba Registradas) ── */
.history-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: #f8f9fb;
  border-radius: 12px;
  margin-bottom: 8px;
  transition: background 0.2s;
}

.history-item:hover { background: #f0f2f6; }
.history-item:last-child { margin-bottom: 0; }

.h-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(37, 99, 235, 0.08);
  color: rgb(37, 99, 235);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.h-info { flex: 1; min-width: 0; }

.h-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.h-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #1a1d26;
}

.h-date {
  font-size: 0.625rem;
  color: #b8bcc8;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.h-fields {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.h-field-row {
  font-size: 0.6875rem;
  color: #7b8294;
  word-break: break-word;
}

.h-field-row strong {
  color: #4a5068;
  font-weight: 600;
}

.history-empty {
  text-align: center;
  padding: 40px 20px;
}

.history-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #f4f5f8;
  color: #c0c4d0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.history-empty-text {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #9ca3b4;
}

.history-empty-sub {
  font-size: 0.6875rem;
  color: #c0c4d0;
  margin-top: 4px;
}

/* ── Rodapé fixo ── */
.lf-footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #eef0f4;
  background: #fff;
  flex-shrink: 0;
}

.lf-submit {
  width: 100%;
  padding: 14px;
  background: rgb(37, 99, 235);
  border: none;
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.lf-submit:hover:not(:disabled) {
  background: #1d4ed8;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.3);
}

.lf-submit:active:not(:disabled) { transform: scale(0.98); }
.lf-submit:disabled { opacity: 0.65; cursor: default; }
</style>
